// Warn after 15 minutes of no activity, then count down 60s to auto sign-out.
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
export const COUNTDOWN_SECONDS = 60;
// Shared across tabs and survives browser restarts, unlike in-memory timers.
export const LAST_ACTIVITY_KEY = 'lastActivity';
const ACTIVITY_WRITE_INTERVAL_MS = 15 * 1000;

const getLastActivity = () => Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;

// JWT exp claim in millis, or 0 if the token can't be read
const decodeExpiry = (token) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' ? exp * 1000 : 0;
  } catch (e) {
    return 0;
  }
};

// Auth utility functions
export const authService = {
  // Save auth data to localStorage
  login: (token, user, requiresPasswordChange = false) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('requiresPasswordChange', JSON.stringify(requiresPasswordChange));
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  },

  // Remove auth data from localStorage
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('requiresPasswordChange');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  },

  // Record user activity (throttled so frequent events don't hammer storage)
  touchActivity: () => {
    const now = Date.now();
    if (now - getLastActivity() >= ACTIVITY_WRITE_INTERVAL_MS) {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    }
  },

  getLastActivity,

  // Token expiry in millis (0 if missing/unreadable)
  tokenExpiresAt: () => {
    const token = localStorage.getItem('authToken');
    return token ? decodeExpiry(token) : 0;
  },

  // Get current user from localStorage
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Get auth token
  getToken: () => {
    return localStorage.getItem('authToken');
  },

  // Swap in a renewed token (session extend) without touching user data
  updateToken: (token) => {
    localStorage.setItem('authToken', token);
  },

  // Authenticated only with an unexpired token and activity within the idle
  // window (warning + countdown). Stale sessions — e.g. a browser reopened days
  // later, or sessions from before lastActivity existed — are cleared.
  isAuthenticated: () => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    const now = Date.now();
    const expired = decodeExpiry(token) <= now;
    const idle = now - getLastActivity() >= IDLE_TIMEOUT_MS + COUNTDOWN_SECONDS * 1000;
    if (expired || idle) {
      authService.logout();
      return false;
    }
    return true;
  },

  // Check if password change is required
  requiresPasswordChange: () => {
    const value = localStorage.getItem('requiresPasswordChange');
    return value ? JSON.parse(value) : false;
  },

  // Set password change required flag
  setRequiresPasswordChange: (value) => {
    localStorage.setItem('requiresPasswordChange', JSON.stringify(value));
  },

  // Check if user is admin - checks multiple possible role fields
  isAdmin: () => {
    const user = authService.getCurrentUser();
    if (!user) return false;
    
    const role = user.role || user.roleType || user.roleName || user.roleType?.toString();
    return role === 'admin' || role === 'ADMIN' || role?.toLowerCase() === 'admin';
  },

  // Get user's role name
  getUserRole: () => {
    const user = authService.getCurrentUser();
    if (!user) return null;
    return user.role || user.roleType || user.roleName || 'user';
  }
};